<template>
    <div class="freelancer-categorization__prediction__wrapper" data-testid="freelancer-categorization-predictions">
        <div class="profile-edition-categorization-prediction__predictor">
            <div class="profile-edition-categorization-prediction__predictor-title">
                {{ t('profile.creation.predictor.wizard.details.predict.expertise.title') }}
            </div>
            <CategorizationSelectionList
                data-testid="freelancer-categorization-prediction"
                :categories="categories"
                :selected-categories="selectedCategories"
                @categorization-selection:update="handleCategorizationSelectionUpdate"
            />
            <VJoyLink
                :href="`#${anchorsPageProfile.categoriesSection}`"
                class="profile-edition-categorization-prediction__not-found"
                data-testid="freelancer-categorization-predictions-switch-to-manual-selection"
                :text="t('profile.creation.completion.wizard.categorization.category.find.help')"
                @click="switchToManualSelection"
            ></VJoyLink>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {onMounted, ref} from 'vue';
    import {useTranslation} from '#imports';
    import {VJoyLink} from '@maltjoy/core-vue';
    import type {PredictCategoryResource} from '@api';
    import {anchorsPageProfile} from '~/config/anchors';
    import CategorizationSelectionList from './CategorizationSelectionList.vue';

    export type InputFamilyCategoryEventParameter = {
        action: 'select' | 'unselect';
        familyId: string;
        categoryId: string;
    };
    type EmitEvents = {
        (e: 'manualSelection'): void;
        (e: 'prediction-selection'): void;
        (e: 'inputFamilyCategory', value: InputFamilyCategoryEventParameter): void;
    };

    const emit = defineEmits<EmitEvents>();
    const props = defineProps<{
        categories: PredictCategoryResource[];
        selectedCategories: string[];
    }>();

    const {t} = useTranslation();
    const selectedCategories = ref<string[]>(props.selectedCategories);

    onMounted(() => {
        if (categoriesAlreadySelectedManually()) {
            emit('manualSelection');
        }
        selectedCategories.value = [...props.selectedCategories];

        if (selectedCategories.value.length === 0) {
            emit('prediction-selection');
        }
    });

    // Handlers
    function handleCategorizationSelectionUpdate(newSelectedCategories?: string[]) {
        const addedValues = newSelectedCategories ? newSelectedCategories?.filter((categ) => !selectedCategories.value.includes(categ)) : [];
        const removedValues = newSelectedCategories
            ? selectedCategories.value.filter((categ) => !newSelectedCategories?.includes(categ))
            : selectedCategories.value;

        addedValues.forEach((addedCategory: string) => {
            const prediction = props.categories.find((pred) => pred.categoryId === addedCategory)!;
            emit('inputFamilyCategory', {action: 'select', familyId: prediction.familyId, categoryId: addedCategory});
        });

        removedValues.forEach((removedCategory: string) => {
            const prediction = props.categories.find((pred) => pred.categoryId === removedCategory)!;
            emit('inputFamilyCategory', {action: 'unselect', familyId: prediction.familyId, categoryId: removedCategory});
        });

        selectedCategories.value = newSelectedCategories || [];
    }

    function categoriesAlreadySelectedManually(): boolean {
        const categoryPredictedIds = props.categories.map((prediction) => prediction.categoryId);
        if (categoryPredictedIds.length >= props.selectedCategories.length) {
            return !props.selectedCategories.every((cat) => categoryPredictedIds.includes(cat));
        }
        return true;
    }

    function switchToManualSelection(): void {
        emit('manualSelection');
    }
</script>
<style lang="scss">
    .freelancer-categorization__prediction__wrapper {
        width: 100%;
    }
</style>
<style scoped lang="scss">
    .profile-edition-categorization-prediction {
        &__not-found {
            font-size: var(--joy-font-size-primary-300);
            margin-top: var(--joy-core-spacing-5);
        }

        &__predictor {
            display: flex;
            flex-direction: column;
            padding: var(--joy-core-spacing-6);
            background-color: var(--joy-color-secondary-10);
            border-radius: var(--joy-core-radius-3);
            margin-bottom: var(--joy-core-spacing-6);

            &-title {
                color: var(--joy-color-neutral-60);
                margin-bottom: var(--joy-core-spacing-5);
            }
        }
    }
</style>
